import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Button } from 'primeng/button';
import { MenuItem, MenuItemCommandEvent, PrimeTemplate } from 'primeng/api';
import { SpeedDialModule } from 'primeng/speeddial';
import { DynamicComponentLoaderService } from '../../../services/dynamic-component-loader.service';
import { NgClass, NgComponentOutlet } from '@angular/common';
import { StepperModule } from 'primeng/stepper';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { DataLayer } from '../../../models/data-layer';
import { CardModule } from 'primeng/card';
import { SidebarModule } from 'primeng/sidebar';
import { DividerModule } from 'primeng/divider';
import { getState } from '@ngrx/signals';
import { FastSwitchDataLayerComponent } from '../../atoms/fast-switch-data-layer/fast-switch-data-layer.component';
import {
  CustomGridStackWidget,
  GridWidgetConfigService,
  WIDGET_TYPE_DISPLAY,
  WidgetType,
} from '../../../services/grid-widget-config.service';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuModule } from 'primeng/menu';
import { FastSwitchIndicatorComponent } from '../../atoms/fast-switch-indicator/fast-switch-indicator.component';
import { Indicator } from '../../../models/indicator';
import { FastSwitchFeatureComponent } from '../../atoms/fast-switch-feature/fast-switch-feature.component';
import { FeatureProperties } from '../../../models/geo-data';
import { WidgetHelperService } from '../../../services/widget/widget-helper.service';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';

import { GlobalsStore } from '../../../stores/globals.state';
import { HeaderComponent } from '../../atoms/header/header.component';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { GridStore } from '../../../stores/grid/grid.state';
import { WegweiserFastSwitchComponent } from '../../molecules/wegweiser-fast-switch/wegweiser-fast-switch.component';
import { WegweiserStore } from '../../../stores/wegweiser-api/wegweiser.state';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { GeoGroupDropdownComponent } from '../../atoms/geo-group-dropdown/geo-group-dropdown.component';

@Component({
  selector: 'app-custom-widget',
  standalone: true,
  imports: [
    Button,
    PrimeTemplate,
    SpeedDialModule,
    NgComponentOutlet,
    StepperModule,
    CardModule,
    SidebarModule,
    DividerModule,
    FastSwitchDataLayerComponent,
    StyleClassModule,
    MenuModule,
    NgClass,
    FastSwitchDataLayerComponent,
    FastSwitchIndicatorComponent,
    FastSwitchFeatureComponent,
    HeaderComponent,
    WegweiserFastSwitchComponent,
    GeoGroupDropdownComponent,
  ],
  templateUrl: './custom-widget.component.html',
  styleUrl: './custom-widget.component.scss',
})
export class CustomWidgetComponent implements OnInit {
  readonly widgetStore = inject(WidgetStore);
  readonly wegweiserStore = inject(WegweiserStore);
  readonly layerStore = inject(DataLayerStore);
  readonly globalStore = inject(GlobalsStore);
  readonly gridStore = inject(GridStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly geoStore = inject(GeoDataStore);
  readonly activeUserStore = inject(ActiveUserStore);

  @Input() id!: string;
  @Input() initialType?: WidgetType;
  @Input() fromBookmark = false;
  @Input() export = false;
  @Output() removeWidget = new EventEmitter<string>();
  @Output() configWidget = new EventEmitter<string>();
  @Output() widgetInitialized = new EventEmitter<string>();
  @ViewChild('widgetContainer', { static: false }) widgetContainer!: ElementRef;

  items: MenuItem[] = [];
  widgetMenu: MenuItem[] = [];
  activeWidgetComponent?: any;
  activeWidget: CustomGridStackWidget | null = null;
  widgetType = '';
  widgetIndicatorIds: string[] = [];
  backgroundColor = '';
  widgetCaption = '';
  readonly = false;
  disableControls = false;

  constructor(
    private dynamicLoader: DynamicComponentLoaderService,
    protected widgetHelper: WidgetHelperService,
    protected widgetConfigService: GridWidgetConfigService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      const gridState = getState(this.gridStore);
      this.setActiveWidget();

      if (gridState.readonly !== this.readonly) {
        this.readonly = !!gridState.readonly;
        this.initMenu();
      }

      this.disableControls =
        !!gridState.activeBookmark?.disableControls &&
        this.activeUserStore.context() === 'atlasExternal';

      if (this.activeWidget) {
        this.widgetIndicatorIds = [...this.activeWidget.widget.indicators];

        this.backgroundColor =
          this.activeWidget.widget?.text?.backgroundColor || '#ffffff';
      }

      if (
        !this.widgetCaption ||
        widgetState.rerenderId === this.id ||
        widgetState.rerenderIds.includes(this.id)
      ) {
        this.setWidgetCaption();
      }
    });

    this.items = (
      Object.entries(WIDGET_TYPE_DISPLAY) as [
        WidgetType,
        { icon: string; label: string },
      ][]
    ).map(([type, display]) => ({
      icon: display.icon,
      type,
      tooltip: display.label,
    }));
  }

  /**
   * Sets the caption for the currently active widget. The caption is determined based on the context of the widget and retrieved from the appropriate store.
   *
   * @return {void} Does not return a value.
   */
  setWidgetCaption(): void {
    if (!this.activeWidget) return;
    this.widgetCaption =
      this.activeWidget.widget.context !== 'external'
        ? this.widgetStore.widgetCaptionById(this.activeWidget._id)
        : this.wegweiserStore.getWidgetCaption(this.activeWidget.widget);
  }

  getGeoId() {
    if (!this.activeWidget || !this.activeWidget.widget.dataLayer) return '';
    return this.layerStore.getGeoDataIdById(this.activeWidget.widget.dataLayer);
  }

  /**
   * Initializes the menu for the widget. Sets up various menu actions and their corresponding commands.
   * Defines menu items such as copying the widget, downloading data, editing the title, toggling fullscreen,
   * and exporting the widget as an image.
   *
   * @return {void} No return value.
   */
  private initMenu() {
    this.widgetMenu = [
      {
        visible: !this.readonly,
        label: 'Kopieren',
        icon: 'fas fa-copy',
        command: (event: MenuItemCommandEvent) => {
          const widgetCopy = this.widgetStore.getAsCopy(this.id);
          if (widgetCopy)
            this.globalStore.triggerAtlasEvent('pasteWidgetCopy', {
              widgetCopy,
            });
        },
      },
      {
        label: 'Datenblatt herunterladen',
        icon: 'fas fa-file-excel',
        command: (event: MenuItemCommandEvent) => {
          const widget = this.widgetStore.get(this.id)?.widget;
          if (!widget || !widget.groupProperty) return;
          const convertTable = this.widgetStore.convertChartTo2DArray(
            widget.chartData,
            widget.groupProperty
          );
          this.dataStore.convertChartDataToXlsx({
            exportData: [convertTable],
            filename: this.widgetStore.widgetCaptionById(this.id, true),
            source: `© ${this.globalStore.sourceString()}`,
          });
        },
      },
      {
        visible: !this.readonly,
        label: 'Überschrift',
        icon: 'fas fa-heading',
        command: (event: MenuItemCommandEvent) => {
          this.widgetStore.openConfig(this.id);
        },
      },
      {
        label: 'Vollbild',
        icon: 'fas fa-expand',
        command: (event: MenuItemCommandEvent) => {
          this.widgetStore.update(this.id, { fullscreen: true });
        },
      },
      {
        label: 'Als Grafik exportieren',
        icon: 'fas fa-download',
        command: (event: MenuItemCommandEvent) => {
          //this.exportMapAsImage();
          this.widgetStore.setWidgetToExport(this.id);
        },
      },
      {
        separator: true,
      },
      {
        label: `ID: ${this.id}`,
        icon: 'fas fa-key',
        styleClass: 'text-color-secondary text-xs',
        tooltip: 'ID der Darstellung',
        command: (event: MenuItemCommandEvent) => {
          if (this.activeUserStore.context() !== 'atlasInternal') return;
          this.confirmationDialogService.showMessage(
            'Schlüssel kopiert',
            'Sie können die Ansicht in der Datenpflege mit diesem Schlüssel finden'
          );
          navigator.clipboard.writeText(this.id);
          console.info('Widget-Configuration: ', this.activeWidget);
        },
      },
    ];
  }
  /**
   * Initializes the component after Angular first displays the data-bound properties
   * and sets up the widget menu with various commands for widgets.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.initMenu();
    setTimeout(() => {
      if (this.initialType && !this.fromBookmark)
        this.setWidgetType(this.initialType);
      if (this.initialType) this.setWidgetCssClassByType(this.initialType);
    }, 300);
  }

  /**
   * Sets the type of the widget and updates its configuration and appearance accordingly.
   *
   * @param {WidgetType} widgetType - The type of widget to be set.
   * @return {void} - This method does not return a value.
   */
  setWidgetType(widgetType: WidgetType): void {
    this.widgetStore.openConfig(this.id);
    this.widgetStore.update(this.id, {
      type: widgetType,
      widget: this.widgetConfigService.getWidgetConfig({}, widgetType),
    });
    this.setWidgetCssClassByType(widgetType);
    this.widgetInitialized.emit();
  }

  /**
   * Sets the geographical group for the widget and updates its chart data.
   *
   * @return {void} This method does not return a value.
   * @param $event
   */
  setWidgetGeoGroup($event: { group: string; dataLayerId: string }): void {
    this.widgetStore.setWidgetProperty(
      'activeGroup',
      $event.group,
      this.id,
      false,
      'centerMap'
    );

    this.widgetStore.updateDistricts(this.id, $event.dataLayerId);
    this.widgetStore.updateChartData(this.id);
    this.saveLayout();
  }

  /**
   * Updates the chart data for the widget by invoking the relevant update method
   * from the widget store and saving the current layout configuration.
   *
   * @return {void} Does not return a value.
   */
  updateChartData(): void {
    this.widgetStore.updateChartData(this.id);
    this.saveLayout();
  }

  /**
   * Sets a CSS class on the active widget element based on the provided widget type.
   *
   * @param {WidgetType} widgetType - The type of the widget used to determine the CSS class to add.
   * @return {void} Does not return a value.
   */
  setWidgetCssClassByType(widgetType: WidgetType): void {
    const widgetEl = this.activeWidget?.el as HTMLElement;
    if (widgetEl) {
      const wrapperDiv = widgetEl.querySelector('.grid-stack-item-content');
      wrapperDiv?.classList.add('grid-stack-item--' + widgetType);
    }
  }
  /**
   * Removes a widget by emitting its associated ID through the `removeWidget` event.
   *
   * @return {void} Does not return any value.
   */
  remove(): void {
    this.removeWidget.emit(this.id);
  }

  /**
   * Configures the widget by emitting its identifier, selecting it in the widget store,
   * and opening its configuration interface.
   *
   * @return {void}
   */
  config(): void {
    this.configWidget.emit(this.id);
    this.widgetStore.select(this.id);
    this.openConfig();
  }

  /**
   * Whether this widget still needs its data/indicator picked - i.e. it has
   * neither a single `indicator`, nor any `indicators`, nor a `dataLayer` set.
   * Different widget types rely on different combinations of these three
   * fields (e.g. a map may only carry a `dataLayer`, a chart only `indicators`),
   * so all three are checked rather than just `indicators` to catch every type.
   *
   * @return {boolean} True if the widget is still an empty, unconfigured shell.
   */
  needsConfiguration(): boolean {
    if (!this.activeWidget) return false;
    const widget = this.activeWidget.widget;
    return (
      !widget.indicator &&
      (!widget.indicators || widget.indicators.length === 0) &&
      !widget.dataLayer
    );
  }

  /**
   * Human-readable label for the "Einstellen"-overlay, e.g. "Balkendiagramm
   * einstellen" - reuses the same type→label mapping as the widget-type
   * picker (`items`) so both stay in sync.
   *
   * @return {string} The label to show on the configure-overlay.
   */
  getConfigureLabel(): string {
    const typeLabel = this.items.find(
      item => (item as any)['type'] === this.activeWidget?.type
    )?.['tooltip'];
    return typeLabel ? `${typeLabel} einstellen` : 'Einstellen';
  }

  /**
   * Sets the active widget based on the current ID and updates the widget configuration.
   *
   * @param {MouseEvent} [event] - Optional mouse event that triggered this action.
   * @return {void} Does not return a value.
   */
  setActiveWidget(event?: MouseEvent): void {
    this.activeWidget = this.widgetStore.get(this.id);
    this.widgetType = this.activeWidget?.type || 'adder';
    if (this.widgetType !== 'adder') {
      this.activeWidgetComponent = this.dynamicLoader.getWidgetConfig(
        this.widgetType,
        { id: this.id }
      );
    } else {
      this.activeWidgetComponent = undefined;
    }
  }

  /**
   * Opens the configuration for a widget. If there is no active configuration, it triggers
   * the widgetStore to open the configuration associated with the current widget id.
   *
   * @return {void} No return value.
   */
  openConfig(): void {
    setTimeout(() => {
      if (!this.widgetStore.activeConfig()) {
        this.widgetStore.openConfig(this.id);
      }
    }, 500);
  }

  /**
   * Exits fullscreen mode for the current widget by updating its fullscreen state to false.
   *
   * @return {void} Does not return a value.
   */
  endFullscreen(): void {
    this.widgetStore.update(this.id, { fullscreen: false });
  }

  /**
   * Handles the logic when a layer is changed, updating the widget configuration,
   * districts, and chart data associated with the widget.
   *
   * @param {DataLayer} layer - The data layer object containing attributes such as its ID, year, and parent layer type.
   * @return {void} This method does not return any value.
   */
  onLayerChanged(layer: DataLayer): void {
    const widget = this.id ? this.widgetStore.get(this.id) : this.activeWidget;
    if (!widget) return;
    const config = {
      ...widget.widget,
      ...{
        activeGroup:
          layer.geoData &&
          widget.widget.activeGroup &&
          this.geoStore.hasGroup(layer.geoData, widget.widget.activeGroup)
            ? widget.widget.activeGroup
            : '',
        dataLayer: layer._id,
        year: layer.year + '',
        dataLayerType: layer.parent,
      },
    };
    this.widgetStore.updateWidgetConfigById(this.id, config, true);
    this.widgetStore.updateDistricts(this.id, layer._id);
    this.widgetStore.updateChartData(this.id);
    this.saveLayout();
  }

  /**
   * Updates the widget configuration and chart data based on the provided indicator and optional type ID.
   *
   * @param {Indicator} indicator - The indicator object that contains the necessary information for updating.
   * @param {string} [typeId] - An optional parameter representing the type ID for the data layer configuration.
   * @return {void} This method does not return a value.
   */
  onIndicatorChanged(indicator: Indicator, typeId?: string): void {
    const widget = this.id ? this.widgetStore.get(this.id) : this.activeWidget;
    if (!widget) return;
    const config = {
      ...widget.widget,
      ...{
        indicator: indicator._id,
      },
    };
    if (typeId) {
      const layer = this.layerStore.getLatestByParent(typeId);
      config.dataLayerType = typeId;
      config.dataLayer = layer._id;
      config.year = layer.year + '';
      config.activeGroup =
        layer.geoData &&
        widget.widget.activeGroup &&
        this.geoStore.hasGroup(layer.geoData, widget.widget.activeGroup)
          ? widget.widget.activeGroup
          : '';
      this.widgetStore.updateWidgetConfigById(this.id, config, true);
      this.widgetStore.updateDistricts(this.id, layer._id);
    } else {
      this.widgetStore.updateWidgetConfigById(this.id, config, true);
    }

    this.widgetStore.updateChartData(this.id);
    this.saveLayout();
  }

  /**
   * Handles the changes to a specific feature and updates the related widget configuration and data.
   *
   * @param {FeatureProperties} feature - The feature that triggered the change, containing its properties and key.
   * @param {DataLayer} [layer] - Optional parameter representing the data layer associated with the feature that changes. Provides additional layer-specific data such as parent, ID, and year.
   * @return {void} This method does not return any value and primarily performs updates to widget configuration and chart data.
   */
  onFeatureChanged(feature: FeatureProperties, layer?: DataLayer): void {
    const widget = this.id ? this.widgetStore.get(this.id) : this.activeWidget;

    if (!widget) return;
    const config = {
      ...widget.widget,
      ...{
        feature: feature.key,
      },
    };
    if (layer) {
      config.dataLayerType = layer.parent;
      config.dataLayer = layer._id;
      config.year = layer.year + '';

      this.widgetStore.updateWidgetConfigById(this.id, config, true);
      this.widgetStore.updateDistricts(this.id, layer._id);
    } else {
      this.widgetStore.updateWidgetConfigById(this.id, config, true);
    }
    this.widgetStore.updateChartData(this.id);
    this.saveLayout();
  }

  /**
   * Displays information for the specified widget by its ID and sets it as the selected widget globally.
   *
   * @param {string} widgetId - The unique identifier of the widget to show information for.
   * @return {void}
   */
  showInfo(widgetId: string): void {
    this.globalStore.setSelectedWidget(widgetId);
  }

  /**
   * Retrieves the CSS class representing the alignment.
   *
   * @return {string} The CSS class indicating the alignment.
   */
  getCssClass(): string {
    return this.getAlignment();
  }

  /**
   * Retrieves the alignment property of the active widget's text, if available,
   * and prefixes it with 'border-arrow--'. If no alignment is specified,
   * returns an empty string.
   *
   * @return {string} A string representing the formatted alignment value, or an empty string if no alignment is present.
   */
  getAlignment(): string {
    const align = this.activeWidget?.widget?.text?.alignment;
    return align ? 'border-arrow--' + align : '';
  }

  /**
   * Saves the current layout configuration by triggering layout changes
   * after a specified timeout delay.
   *
   * @return {void} Does not return any value.
   */
  saveLayout(): void {
    this.gridStore.triggerLayoutChanges();
  }

  /**
   * Determines if the current widget is a content widget based on its type.
   *
   * @return {boolean} Returns true if the widget is a content widget, otherwise false.
   */
  isContentWidget(): boolean {
    return this.widgetHelper.isContentWidget(this.widgetType);
  }
}
